import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, Clock, XCircle, Users, Settings, FileText, CheckSquare, Square } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';

// ═══════════════════════════════════════════════════════════════════════════════
// DOCUMENT TYPES CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════
const DOCUMENT_TYPES = [
  { value: 'government_id_front', labelKey: 'kyc.docGovIdFrontLabel', descKey: 'kyc.docGovIdFrontDesc' },
  { value: 'government_id_back',  labelKey: 'kyc.docGovIdBackLabel',  descKey: 'kyc.docGovIdBackDesc' },
  { value: 'selfie_with_id',      labelKey: 'kyc.docSelfieLabel',     descKey: 'kyc.docSelfieDesc' },
  { value: 'proof_of_address',    labelKey: 'kyc.docProofAddressLabel',descKey: 'kyc.docProofAddressDesc' },
  { value: 'bank_statement',      labelKey: 'kyc.docBankStatementLabel',descKey: 'kyc.docBankStatementDesc' },
  { value: 'source_of_funds',     labelKey: 'kyc.docSourceFundsLabel', descKey: 'kyc.docSourceFundsDesc' },
  { value: 'employment_letter',   labelKey: 'kyc.docEmploymentLabel',  descKey: 'kyc.docEmploymentDesc' },
  { value: 'tax_id',              labelKey: 'kyc.docTaxIdLabel',       descKey: 'kyc.docTaxIdDesc' },
  { value: 'other',               labelKey: 'kyc.docOtherLabel',       descKey: 'kyc.docOtherDesc' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// DOCUMENT SELECTOR
// ═══════════════════════════════════════════════════════════════════════════════
function DocumentSelector({ level, selected, onChange }) {
  const { t } = useLocale();

  const toggleDoc = (docValue) => {
    if (selected.includes(docValue)) onChange(selected.filter(d => d !== docValue));
    else onChange([...selected, docValue]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-[var(--text-primary)] capitalize">
          {t('kyc.kycDocuments', { level })}
        </p>
        <span className="text-xs text-[var(--text-tertiary)]">
          {t('kyc.selected', { count: selected.length })}
        </span>
      </div>
      <div className="bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg p-3 space-y-2 max-h-64 overflow-y-auto">
        {DOCUMENT_TYPES.map(doc => {
          const isSelected = selected.includes(doc.value);
          return (
            <button
              key={doc.value}
              type="button"
              onClick={() => toggleDoc(doc.value)}
              className={`w-full text-left px-3 py-2 rounded-lg border transition-smooth flex items-start gap-2 hover:bg-[var(--bg-secondary)] ${
                isSelected ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)]/5' : 'border-[var(--border-color)]'
              }`}
            >
              {isSelected
                ? <CheckSquare className="w-4 h-4 text-[var(--accent-primary)] shrink-0 mt-0.5" />
                : <Square className="w-4 h-4 text-[var(--text-tertiary)] shrink-0 mt-0.5" />}
              <div className="flex-1">
                <p className={`text-xs font-medium ${isSelected ? 'text-[var(--accent-primary)]' : 'text-[var(--text-primary)]'}`}>
                  {t(doc.labelKey)}
                </p>
                <p className="text-[0.7rem] text-[var(--text-tertiary)] mt-0.5">{t(doc.descKey)}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN CONFIG PANEL
// ═══════════════════════════════════════════════════════════════════════════════
export function KycConfigPanel({ config, onUpdate }) {
  const { t } = useLocale();

  const parseJsonArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') { try { return JSON.parse(value); } catch { return []; } }
    return [];
  };

  const getDocArray = (configValue, defaultValue) => {
    const parsed = parseJsonArray(configValue);
    return parsed.length > 0 ? parsed : defaultValue;
  };

  const initFormState = (cfg) => ({
    approvalMode:               cfg?.approval_mode || 'manual',
    enforceKycOnDeposit:        Boolean(cfg?.enforce_kyc_on_deposit),
    enforceKycOnWithdrawal:     Boolean(cfg?.enforce_kyc_on_withdrawal),
    depositThresholdBasic:      Number(cfg?.deposit_threshold_basic)      || 1000,
    depositThresholdModerate:   Number(cfg?.deposit_threshold_moderate)   || 5000,
    depositThresholdVerified:   Number(cfg?.deposit_threshold_verified)   || 10000,
    withdrawalThresholdBasic:   Number(cfg?.withdrawal_threshold_basic)   || 500,
    withdrawalThresholdModerate:Number(cfg?.withdrawal_threshold_moderate)|| 2000,
    withdrawalThresholdVerified:Number(cfg?.withdrawal_threshold_verified)|| 5000,
    autoApproveBasic:           Boolean(cfg?.auto_approve_basic),
    autoApproveModerate:        Boolean(cfg?.auto_approve_moderate),
    autoApproveVerified:        Boolean(cfg?.auto_approve_verified),
    requiredDocsBasic:    getDocArray(cfg?.required_docs_basic,    ['government_id_front']),
    requiredDocsModerate: getDocArray(cfg?.required_docs_moderate, ['government_id_front','government_id_back','selfie_with_id','proof_of_address']),
    requiredDocsVerified: getDocArray(cfg?.required_docs_verified, ['government_id_front','government_id_back','selfie_with_id','proof_of_address','bank_statement','source_of_funds']),
  });

  const [form,   setForm]   = useState(initFormState(config));
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (config) setForm(initFormState(config)); }, [config]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate({
        approvalMode:               form.approvalMode,
        autoApproveBasic:           form.autoApproveBasic,
        autoApproveModerate:        form.autoApproveModerate,
        autoApproveVerified:        form.autoApproveVerified,
        enforceKycOnDeposit:        form.enforceKycOnDeposit,
        depositThresholdBasic:      form.depositThresholdBasic,
        depositThresholdModerate:   form.depositThresholdModerate,
        depositThresholdVerified:   form.depositThresholdVerified,
        enforceKycOnWithdrawal:     form.enforceKycOnWithdrawal,
        withdrawalThresholdBasic:   form.withdrawalThresholdBasic,
        withdrawalThresholdModerate:form.withdrawalThresholdModerate,
        withdrawalThresholdVerified:form.withdrawalThresholdVerified,
        requiredDocsBasic:          form.requiredDocsBasic,
        requiredDocsModerate:       form.requiredDocsModerate,
        requiredDocsVerified:       form.requiredDocsVerified,
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">

      {/* ── Document Requirements ── */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[var(--text-tertiary)]" />
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('kyc.requiredDocuments')}</h3>
        </div>
        <p className="text-xs text-[var(--text-secondary)]">{t('kyc.requiredDocsDesc')}</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DocumentSelector level={t('kyc.basic')}    selected={form.requiredDocsBasic}    onChange={(docs) => setForm({ ...form, requiredDocsBasic: docs })} />
          <DocumentSelector level={t('kyc.moderate')} selected={form.requiredDocsModerate} onChange={(docs) => setForm({ ...form, requiredDocsModerate: docs })} />
          <DocumentSelector level={t('kyc.verified')} selected={form.requiredDocsVerified} onChange={(docs) => setForm({ ...form, requiredDocsVerified: docs })} />
        </div>
      </div>

      <div className="border-t border-[var(--border-color)]" />

      {/* ── Approval Mode ── */}
      <div>
        <label className="block text-xs font-medium text-[var(--text-primary)] mb-2">{t('kyc.approvalMode')}</label>
        <div className="flex gap-2">
          {[
            { val: 'manual', label: t('kyc.manualReview') },
            { val: 'auto',   label: t('kyc.autoApprove') },
          ].map(({ val, label }) => (
            <button key={val} type="button" onClick={() => setForm({ ...form, approvalMode: val })}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-smooth ${
                form.approvalMode === val
                  ? 'bg-[var(--accent-primary)] text-white'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'
              }`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Auto-Approve Settings ── */}
      {form.approvalMode === 'auto' && (
        <div className="bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg p-3 space-y-2">
          <p className="text-xs font-medium text-[var(--text-primary)]">{t('kyc.autoApproveLevels')}</p>
          {[
            { key: 'autoApproveBasic',    label: t('kyc.basicKyc') },
            { key: 'autoApproveModerate', label: t('kyc.moderateKyc') },
            { key: 'autoApproveVerified', label: t('kyc.verifiedKyc') },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 text-xs text-[var(--text-secondary)] cursor-pointer">
              <input type="checkbox" checked={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                className="rounded border-[var(--border-color)]" />
              {label}
            </label>
          ))}
        </div>
      )}

      {/* ── Deposit Enforcement ── */}
      <div>
        <label className="flex items-center gap-2 text-xs font-medium text-[var(--text-primary)] mb-2 cursor-pointer">
          <input type="checkbox" checked={form.enforceKycOnDeposit}
            onChange={(e) => setForm({ ...form, enforceKycOnDeposit: e.target.checked })}
            className="rounded border-[var(--border-color)]" />
          {t('kyc.enforceOnDeposit')}
        </label>
        {form.enforceKycOnDeposit && (
          <div className="grid grid-cols-3 gap-2 ml-6">
            {[
              { label: t('kyc.basic'),    key: 'depositThresholdBasic' },
              { label: t('kyc.moderate'), key: 'depositThresholdModerate' },
              { label: t('kyc.verified'), key: 'depositThresholdVerified' },
            ].map(({ label, key }) => (
              <div key={key}>
                <label className="block text-xs text-[var(--text-secondary)] mb-1">{label}</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--text-tertiary)]">$</span>
                  <input type="number" value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: parseFloat(e.target.value) })}
                    className="w-full pl-6 pr-3 py-1.5 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)]" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Withdrawal Enforcement ── */}
      <div>
        <label className="flex items-center gap-2 text-xs font-medium text-[var(--text-primary)] mb-2 cursor-pointer">
          <input type="checkbox" checked={form.enforceKycOnWithdrawal}
            onChange={(e) => setForm({ ...form, enforceKycOnWithdrawal: e.target.checked })}
            className="rounded border-[var(--border-color)]" />
          {t('kyc.enforceOnWithdrawal')}
        </label>
        {form.enforceKycOnWithdrawal && (
          <div className="grid grid-cols-3 gap-2 ml-6">
            {[
              { label: t('kyc.basic'),    key: 'withdrawalThresholdBasic' },
              { label: t('kyc.moderate'), key: 'withdrawalThresholdModerate' },
              { label: t('kyc.verified'), key: 'withdrawalThresholdVerified' },
            ].map(({ label, key }) => (
              <div key={key}>
                <label className="block text-xs text-[var(--text-secondary)] mb-1">{label}</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--text-tertiary)]">$</span>
                  <input type="number" value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: parseFloat(e.target.value) })}
                    className="w-full pl-6 pr-3 py-1.5 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)]" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Info Box ── */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 text-xs text-blue-700 dark:text-blue-400">
        <p className="font-medium mb-1">{t('kyc.howItWorks')}</p>
        <ul className="space-y-0.5 text-[0.7rem]">
          <li>• {t('kyc.howItWorksBullet1')}</li>
          <li>• {t('kyc.howItWorksBullet2')}</li>
          <li>• {t('kyc.howItWorksBullet3')}</li>
          <li>• {t('kyc.howItWorksBullet4')}</li>
        </ul>
      </div>

      {/* ── Save Button ── */}
      <div className="flex gap-2 pt-2 border-t border-[var(--border-color)]">
        <button onClick={handleSave} disabled={saving}
          className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-primary-dark)] text-xs font-medium transition-smooth disabled:opacity-50">
          {saving ? t('kyc.savingConfiguration') : t('kyc.saveConfiguration')}
        </button>
        <a href="/broker/kyc"
          className="px-4 py-2 border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-secondary)] text-xs text-[var(--text-primary)] transition-smooth inline-flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" />
          {t('kyc.openKycDashboard')}
        </a>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// KYC STATS PREVIEW
// ═══════════════════════════════════════════════════════════════════════════════
export function KycStatsPreview({ stats }) {
  const { t } = useLocale();

  if (!stats) {
    return <div className="text-xs text-[var(--text-tertiary)] italic">{t('kyc.enableToViewStats')}</div>;
  }

  const verified = stats.overview?.verified || {};

  return (
    <div className="grid grid-cols-4 gap-2 mt-2">
      <div className="bg-[var(--bg-tertiary)] rounded-lg p-2">
        <p className="text-xs text-[var(--text-tertiary)] mb-0.5">{t('kyc.statNone')}</p>
        <p className="text-sm font-semibold text-[var(--text-primary)]">{verified.none || 0}</p>
      </div>
      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
        <p className="text-xs text-green-600 dark:text-green-400 mb-0.5">{t('kyc.basic')}</p>
        <p className="text-sm font-semibold text-green-700 dark:text-green-300">{verified.basic || 0}</p>
      </div>
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2">
        <p className="text-xs text-blue-600 dark:text-blue-400 mb-0.5">{t('kyc.moderate')}</p>
        <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">{verified.moderate || 0}</p>
      </div>
      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2">
        <p className="text-xs text-purple-600 dark:text-purple-400 mb-0.5">{t('kyc.verified')}</p>
        <p className="text-sm font-semibold text-purple-700 dark:text-purple-300">{verified.verified || 0}</p>
      </div>
    </div>
  );
}

export default { KycConfigPanel, KycStatsPreview };